import {REQUEST_STATUSES, type RequestStatus} from './request';

/**
 * The filters the request list offers, from the interface design rather than from the
 * status enumeration — a reader thinks in "what is waiting for me" and "what is on its
 * way", not in twelve states. Every view therefore covers several statuses.
 *
 * **The chips, and nothing about the day.** Which days a reader is looking at is the tab's
 * question, and the two are independent: the past tab filters by chip exactly as the active
 * one does. `ALL` is the chip that narrows nothing.
 */
export const REQUEST_VIEWS = ['ALL', 'ACTION_REQUIRED', 'IN_PROGRESS', 'ACKNOWLEDGED', 'REJECTED'] as const;

export type RequestView = (typeof REQUEST_VIEWS)[number];

/**
 * Which days the list covers, which is the one thing the chips do not decide.
 *
 * `ACTIVE` is the dispatch days still ahead, `PAST` the ones already over — the design's two
 * tabs, and the `Notes` frame beside the Overview in Figma: "Active: requests for which the
 * steering day is yet to come. Inactive: requests for which the steering day has already
 * passed." The design says steering day where the code says dispatch day.
 */
export const REQUEST_TABS = ['ACTIVE', 'PAST'] as const;

export type RequestTab = (typeof REQUEST_TABS)[number];

/**
 * Which statuses a view covers, as ElaadNL described them on 2026-09-24.
 *
 * `ALL` is absent because it is not defined by status: it is every status, in whichever tab
 * is open.
 *
 * **Every status has a chip.** One added later and not placed here is reachable only through
 * `ALL`, which is visible rather than silent, and is the reason this is a lookup rather than
 * a fallthrough.
 */
export const STATUSES_IN_VIEW: Partial<Record<RequestView, readonly RequestStatus[]>> = {
  /**
   * Work for a person. Approval is the obvious one; a publication that failed or only half
   * happened is the other, because somebody has to decide what to do about it while the day
   * can still be saved.
   */
  ACTION_REQUIRED: ['WAITING_FOR_APPROVAL', 'READY_FOR_PUBLISHING', 'PARTIALLY_PUBLISHED', 'PUBLISHING_FAILED'],
  /**
   * On its way, and nobody has to do anything. `NOT_ACKNOWLEDGED` is here rather than under
   * action required because the reports are still being polled (ADR 0026) — it is silence
   * from a VEN, not a task. It is rarely seen in the active tab, since the status is only
   * reached once the dispatch window has closed.
   */
  IN_PROGRESS: ['PUBLISHING', 'PUBLISHED', 'NOT_ACKNOWLEDGED'],
  /** Receipted, whether by every group or only some. */
  ACKNOWLEDGED: ['ACKNOWLEDGED', 'PARTIALLY_ACKNOWLEDGED'],
  /**
   * Never dispatched. Refused at approval, withdrawn before it went out, or expired at the
   * publication deadline with nobody having decided.
   *
   * **`EXPIRED` belongs here rather than in the past tab**, which is the question that made
   * the difference: the deadline falls on the day *before* the dispatch day, so for sixteen
   * hours afterwards the day is still ahead and the request is still on the active list.
   * Filing it by day alone would leave it there with no chip at all. It is not on its way
   * and nobody can act on it — it did not go out, and that is what this chip says.
   */
  REJECTED: ['DISAPPROVED', 'CANCELLED', 'EXPIRED'],
};

/**
 * The statuses a view covers, `ALL` included — which is every status, because it is the chip
 * that narrows nothing.
 *
 * A function rather than reaching into the lookup, so no caller has to remember what an
 * absent key means and none of them indexes an object with a variable.
 */
const STATUSES_BY_VIEW = new Map<RequestView, readonly RequestStatus[]>(
  Object.entries(STATUSES_IN_VIEW).map(([view, statuses]) => [view as RequestView, statuses]),
);

export const statusesInView = (view: RequestView): readonly RequestStatus[] =>
  STATUSES_BY_VIEW.get(view) ?? REQUEST_STATUSES;

/** Whether the tab looks backwards, which also inverts the ordering. */
export const isPast = (tab: RequestTab): boolean => tab === 'PAST';
